import useCurrentProduct from "@/services/product/hooks/use-current-product.ts";
import type {
    Choice,
    ChoiceWithPossibleIndicator,
    Combination,
    CombinationKey,
    ProductPropertyKey,
} from "@/features/sub-families/types";
import { getCombinationKeys } from "@/features/sub-families/utils/change-filter-option.ts";
import { pickCombinationConfigCodes } from "@/lib/product.ts";
import { useAppSelector } from "@/redux/store.ts";
import type { Selection } from "@/types";
import { typedObjectEntries } from "@/utils/types.ts";

export default function useChoicesWithPossibility(choices: Choice[], productProperty: ProductPropertyKey) {
    const currentProduct = useCurrentProduct();

    const combinations = useAppSelector((state) => state.filterOptions.combinations);
    const smartNumberMapping = useAppSelector((state) => state.smartNumber.mapping);

    const combinationKeys = getCombinationKeys(currentProduct);

    // * TL;DR; We do not care about the massCode when displaying options that should or should not be combinable
    // * When only the massCode is incompatible, this will automatically be updated when changing the detected product
    const baseSelection = pickCombinationConfigCodes(currentProduct, null, smartNumberMapping);

    return choices.map<ChoiceWithPossibleIndicator>((choice) => {
        let isPossible = true;

        const combinationKey = getCombinationKey(productProperty);

        const selection = combinationKeys.includes(combinationKey)
            ? { ...baseSelection, [productProperty]: choice.configCode }
            : null;

        if (selection) {
            const available = getAvailable(combinationKey, selection, combinations);
            const targetValue = selection[combinationKey];

            isPossible = targetValue !== null ? available.has(targetValue) : true;
        }

        return { ...choice, isPossible };
    });
}

// region Helpers
function getCombinationKey(productPropertyKey: ProductPropertyKey): CombinationKey {
    return productPropertyKey as CombinationKey;
}

function getAvailable(key: CombinationKey, selection: Selection, combinations: Combination[]) {
    const otherSelectionKeys = typedObjectEntries(selection)
        .map(([selectionKey]) => selectionKey)
        .filter((selectionKey) => selectionKey !== key);

    const values = combinations
        .filter((combination) => {
            return otherSelectionKeys.every((selectionKey) => {
                return selection[selectionKey] === null || selection[selectionKey] === combination[selectionKey];
            });
        })
        .map((combination) => combination[key]);

    return new Set(values);
}
// endregion
